/**
 * The record's story, newest first: calls with what was said and decided on them, notes, and
 * finished work. A note is typed straight in at the top. Entries are never edited; a correction
 * is another note, the way a paper file would carry it.
 */
import {
  activityKindLabel,
  callOutcomeLabel,
  dispositionLabel,
  formatPhone,
  type Activity,
} from "@tlcrm/shared";
import { useState } from "react";

import { errorMessage } from "../api/client";
import { useAddNote, useTimeline } from "../api/hooks";
import { duration, when } from "../lib/format";
import { Icon } from "./Icon";
import type { IconName } from "./icons";
import { useToast } from "./Toasts";
import { EmptyState, ErrorState } from "./ui";

type Kind = "contacts" | "leads" | "policies";
const ABOUT: Record<Kind, "contactId" | "leadId" | "policyId"> = {
  contacts: "contactId",
  leads: "leadId",
  policies: "policyId",
};

function iconFor(a: Activity): IconName {
  if (a.kind === "call") return a.call?.direction === "inbound" ? "phoneIn" : "phoneOut";
  if (a.kind === "note") return "message";
  if (a.kind === "task_completed") return "check";
  if (a.kind === "email") return "file";
  return "info";
}

function title(a: Activity): string {
  if (a.kind === "call" && a.call) {
    const way = a.call.direction === "inbound" ? "Inbound call" : "Outbound call";
    if (a.call.disposition) return `${way} · ${dispositionLabel(a.call.disposition)}`;
    if (a.call.outcome && a.call.outcome !== "answered")
      return `${way} · ${callOutcomeLabel(a.call.outcome)}`;
    return way;
  }
  if (a.kind === "task_completed") return `Completed: ${a.subject}`;
  return a.subject || activityKindLabel(a.kind);
}

function Entry({ a }: { a: Activity }) {
  const c = a.call;
  const body = c ? [c.summary, c.notes].filter(Boolean).join("\n\n") || a.body : a.body;
  return (
    <li className={`tl-item${a.pinned ? " is-pinned" : ""}`}>
      <span className="tl-icon">
        <Icon name={iconFor(a)} />
      </span>
      <div>
        <div className="tl-head">
          <b>{title(a)}</b>
          <span className="when" title={new Date(a.occurredAt).toLocaleString("en-US")}>
            {when(a.occurredAt)}
          </span>
        </div>
        <div className="tl-meta">
          {(c?.agent ?? a.actor) && <span>{(c?.agent ?? a.actor)!.fullName}</span>}
          {c && c.durationSeconds !== null && c.outcome === "answered" && (
            <span className="mono">{duration(c.durationSeconds)}</span>
          )}
          {c?.reason && <span>{c.reason}</span>}
          {c?.line && <span>{c.line} line</span>}
          {c?.queue && <span>{c.queue}</span>}
          {c?.phone && <span className="mono">{formatPhone(c.phone)}</span>}
          {c?.transferredTo && <span>Transferred to {c.transferredTo}</span>}
        </div>
        {body && <p className="tl-body">{body}</p>}
      </div>
    </li>
  );
}

export function Timeline({ kind, id }: { kind: Kind; id: string }) {
  const timeline = useTimeline(kind, id);
  const addNote = useAddNote();
  const toast = useToast();
  const [note, setNote] = useState("");

  const save = async () => {
    if (!note.trim()) return;
    try {
      await addNote.mutateAsync({ body: note, [ABOUT[kind]]: id });
      setNote("");
    } catch (err) {
      toast(errorMessage(err), { error: true });
    }
  };

  const items = timeline.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="card">
      <div className="card-head">
        <h3>Activity</h3>
      </div>
      <div className="composer">
        <label className="field-label" htmlFor={`note-${id}`}>
          Add a note
        </label>
        <textarea
          id={`note-${id}`}
          className="field"
          value={note}
          placeholder="What should the next person to open this record know?"
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void save();
          }}
        />
        <div className="row">
          <span className="hint">
            <kbd>Ctrl</kbd> <kbd>↵</kbd> to save
          </span>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => void save()}
            disabled={!note.trim() || addNote.isPending}
          >
            {addNote.isPending ? "Saving…" : "Save note"}
          </button>
        </div>
      </div>
      {timeline.isError ? (
        <ErrorState error={timeline.error} onRetry={() => void timeline.refetch()} />
      ) : timeline.isPending ? (
        <div className="rel-empty">Loading activity…</div>
      ) : items.length === 0 ? (
        <EmptyState icon="clock" title="Nothing yet">
          Calls from the desk land here on their own, with the outcome and the agent's summary.
        </EmptyState>
      ) : (
        <ul className="tl">
          {items.map((a) => (
            <Entry key={a.id} a={a} />
          ))}
        </ul>
      )}
      {timeline.hasNextPage && (
        <div className="tl-more">
          <button
            className="btn btn-sm"
            onClick={() => void timeline.fetchNextPage()}
            disabled={timeline.isFetchingNextPage}
          >
            {timeline.isFetchingNextPage ? "Loading…" : "Older activity"}
          </button>
        </div>
      )}
    </div>
  );
}
